import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1548690312-e3b507d8c110?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxzdHJlZXR3ZWFyJTIwZml0bmVzcyUyMHdvbWFufGVufDB8fHx8MTc4OTQwNDUyNXww&ixlib=rb-4.1.0&q=85";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AuthPage({ mode = "login" }) {
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { login, register } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      if (isLogin) await login(email, password);
      else await register(name || email.split("@")[0], email, password);
      nav("/");
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 relative" style={{ background: "#0A0A0A" }}>
      <div
        className="relative hidden md:block concrete"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.85) 100%), url(${HERO})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 p-10 flex flex-col justify-between text-white">
          <div
            className="font-display font-black text-3xl"
            style={{ letterSpacing: "-0.04em" }}
            data-testid="brand-logo"
          >
            CINDR <span style={{ color: "#FF0055" }}>REBUILD</span>
          </div>
          <div>
            <p className="eyebrow mb-4" style={{ color: "#FF0055" }}>The CINDR promise</p>
            <h1 className="font-display font-black text-4xl lg:text-5xl leading-[0.95] mb-6">
              YOU DON'T FIND<br/>THE NEXT VERSION<br/>OF YOURSELF.<br/>
              <span style={{ color: "#FF0055" }}>YOU BUILD HER.</span>
            </h1>
            <p className="text-zinc-300 max-w-md">
              Reset, don't restart. Keep the promise. Record the proof.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10 relative z-10">
        <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid="auth-form">
          <p className="eyebrow mb-2">{isLogin ? "Welcome back" : "Start the rebuild"}</p>
          <h2 className="font-display font-black text-3xl md:text-4xl mb-8 brand-underline">
            {isLogin ? "SIGN IN" : "BUILD HER"}
          </h2>

          {!isLogin && (
            <div className="mb-4">
              <label className="label">Name</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First name"
                data-testid="input-name"
              />
            </div>
          )}
          <div className="mb-4">
            <label className="label">Email</label>
            <input
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="input-email"
            />
          </div>
          <div className="mb-6">
            <label className="label">Password</label>
            <input
              className="input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              data-testid="input-password"
            />
          </div>

          {err && (
            <p className="mb-4 text-sm" style={{ color: "#EF4444" }} data-testid="auth-error">
              {err}
            </p>
          )}

          <button
            type="submit"
            className="btn-primary w-full pulse-brand"
            disabled={busy}
            data-testid="submit-auth"
          >
            {busy ? "…" : isLogin ? "Sign in" : "Create account"}
          </button>

          <button
            type="button"
            className="btn-ghost w-full mt-4"
            onClick={() => { setIsLogin(!isLogin); setErr(null); }}
            data-testid="toggle-auth"
          >
            {isLogin ? "New to CINDR Rebuild? Build her ↗" : "Have an account? Sign in ↗"}
          </button>

          <p className="mt-8 text-xs text-zinc-500 uppercase tracking-widest">
            Confidence is practised before it is felt.
          </p>
        </form>
      </div>
    </div>
  );
}
